import React, { ReactNode, useCallback, useEffect, useState, useMemo } from 'react';
import { Badge, Button, Card, CardBody, CodeDisplay, DataTable, DateDisplay, Icon } from '@aidkitorg/component-library';
import { ColumnDef } from '@tanstack/react-table';
import { BadgeVariant } from '@aidkitorg/component-library/dist/types/src/Badge';
import { usePayments } from '../../../Contexts/PaymentsContext';
import { PaymentBatchRecipient } from '@aidkitorg/aidkit/lib/admin_panel/payments';
import { useHistory, useLocation, useParams, NavLink } from 'react-router-dom';
import { paymentTypeToString } from '../../../utils/payment_utils';
import { PaymentBatchRecipientFilter, PaymentBatchRecipientFilters } from './PaymentBatchRecipientsFilter';
import { SelectOptionType } from '@aidkitorg/component-library/dist/types/src/Select';
import { useVisibilityPolling } from '../../../Hooks/VisibilityPolling';
import { useLocalizedStrings } from '../../../Localization';
import type { PaymentDetails } from '@aidkitorg/types/lib/survey';

export const PaymentBatchRecipientsDataTable: React.FC<{
  lastUpdatedTimestamp?: number,
  toggleButton: React.ReactElement<typeof Button>,
  pageConfig?: PaymentDetails,
  isConfigLoading: boolean
}> = ({
  lastUpdatedTimestamp,
  toggleButton,
  pageConfig,
  isConfigLoading
}) => {
  const L = useLocalizedStrings();
  const history = useHistory();

  const { batch_name } = useParams<{ batch_name: string }>()
  
  const [paymentRecipientsDetails, setPaymentRecipientsDetails] = useState<Array<PaymentBatchRecipient>>([])
  const { getBatchRecipientDetails, fetchRecipientDetailsByBatch, getIsBatchLoading, getBatchTargetFields } = usePayments();

  const [filters, setFilters] = useState<PaymentBatchRecipientFilters>({
    paymentStatus: [],
    paymentMethod: '',
    paymentInitiatedDateStart: '',
    paymentInitiatedDateEnd: '',
  })
  const [activeFilters, setActiveFilters] = useState<number>(0)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})

  const isPaymentBatchLoading = getIsBatchLoading(batch_name)
  const targetFields = getBatchTargetFields(batch_name)

  const location = useLocation()
  const isOnPaymentBatchRecipientsPage = location.pathname.includes('/admin-panel/payments/payment-details')

  // URL parameter helpers for column visibility
  const getColumnsFromUrl = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const hiddenParam = params.get('hidden');

    return {
      hidden: hiddenParam ? hiddenParam.split(',').filter(Boolean) : []
    };
  }, [location.search]);

  const updateUrlWithColumns = useCallback((visibility: Record<string, boolean>) => {
    const params = new URLSearchParams(location.search);
    const hiddenColumns = Object.entries(visibility)
      .filter(([_, visible]) => !visible)
      .map(([columnId]) => columnId);

    // Only set hidden param if there are hidden columns
    if (hiddenColumns.length > 0) {
      params.set('hidden', hiddenColumns.join(','));
    } else {
      params.delete('hidden');
    }

    const newUrl = `${location.pathname}?${params.toString()}`;
    history.replace(newUrl);
  }, [location.search, location.pathname, history]);

  const PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS = useMemo((): ColumnDef<PaymentBatchRecipient>[] => {
    const baseColumns: ColumnDef<PaymentBatchRecipient>[] = [
      {
        id: 'uuid',
        accessorKey: "uuid",
        header: 'UID',
        enablePinning: true,
        cell: ({ row }) => {
          const uuid = row.original.uuid
          const [copied, setCopied] = useState(false)
    
          const handleCopy = async () => {
            try {
              await navigator.clipboard.writeText(uuid)
              setCopied(true)
              setTimeout(() => setCopied(false), 2000)
            } catch (err) {
              console.error("Failed to copy: ", err)
            }
          }
    
          return (
            <div className="flex items-center gap-2">
              <span className="truncate">{uuid}</span>
              <Button 
                icon={
                  <Icon role="decorative" name={copied ? "CheckIcon" : "ClipboardDocumentIcon"}/>
                }
                variant="secondary" 
                size="sm"
                aria-label={L.admin_panel.payments.uuid_desc}
                onClick={handleCopy}
              />
            </div>
          )
        }
      },
      {
        id: 'firstName',
        accessorKey: "firstName",
        header: L.admin_panel.payments.first_name,
      },
      {
        id: 'lastName',
        accessorKey: "lastName",
        header: L.admin_panel.payments.last_name,
      },
      {
        id: 'paymentStatus',
        accessorKey: "paymentStatus",
        header: L.admin_panel.payments.payment_status,
        cell: ({ row }) => {
          const status = row.original.paymentStatus
    
          if (status === 'unknown') {
            return <span>--</span>
          }
    
          let variant: BadgeVariant;
          let copy = '';
    
          switch (status) {
            case 'eligible': 
              variant = 'neutral'
              copy = L.admin_panel.payments.eligible
              break;
            case 'pending':
              variant = 'info'
              copy = L.admin_panel.payments.pending
              break;
            case 'paused':
              variant = 'warning'
              copy = L.admin_panel.payments.paused
              break;
            case 'paid':
              variant = 'success'
              copy = L.admin_panel.payments.paid
              break;
            case 'failed':
              variant = 'error'
              copy = L.admin_panel.payments.failed
              break;
            case 'scheduled':
              variant = 'info'
              copy= L.admin_panel.payments.scheduled
              break;
            default: 
              variant = 'neutral'
              copy = L.admin_panel.payments.eligible
          }
    
          return (
            <Badge variant={variant} >{copy}</Badge>
          )
        },
        filterFn: (row, columnId, filterValue) => {
          if (filterValue.length !== 0) {
            const status = row.getValue(columnId)
  
            return filterValue.find((v: string) => v === status) !== undefined
          }
          return true;
        }
      },
      {
        id: 'paymentAmount',
        accessorKey: "paymentAmount",
        header: L.admin_panel.payments.payment_amount,
        cell: ({ row }) => (
          <span>${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.original.paymentAmount)}</span>
        )
      },
      {
        id: 'email',
        accessorKey: "email",
        header: L.admin_panel.payments.email,
        cell: ({ row }) => (
          <div className="truncate">{row.original.email}</div>
        )
      },
      {
        id: 'phone',
        accessorKey: "phone",
        header: L.admin_panel.payments.phone_number,
      },
      {
        id: 'paymentInitiatedDate',
        accessorKey: "paymentInitiatedDate",
        header: L.admin_panel.payments.payment_initiated,
        cell: ({ row }) => {
          if (row.original.paymentInitiatedDate === '') return "--"
          const date = new Date(row.original.paymentInitiatedDate)
          return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }).format(date)
        },
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue?.paymentInitiatedDateStart || !filterValue?.paymentInitiatedDateEnd) return true
          const date = row.getValue(columnId) as Date
          return (
            date >= new Date(filterValue.paymentInitiatedDateStart) && 
          date <= new Date(filterValue.paymentInitiatedDateEnd)
          )
        }
      },
      {
        id: 'paymentMethod',
        accessorKey: "paymentMethod",
        header: L.admin_panel.payments.payment_method,
        cell: ({ row }) => {
          const paymentMethod = paymentTypeToString(row.original.paymentMethod)
    
          return <span>{paymentMethod}</span>
        },
        filterFn: (row, columnId, filterValue) => {
          const paymentMethod = paymentTypeToString(row.getValue(columnId))
        
          return paymentMethod === filterValue
        }
      },
      {
        id: 'paymentScheduledFor',
        accessorKey: 'paymentScheduledFor',
        header: L.admin_panel.payments.payment_scheduled_for,
        cell: ({ row }) => {
          if (!row.original.paymentScheduledFor || row.original.paymentScheduledFor === '') return "--"
          const date = new Date(row.original.paymentScheduledFor)
          return new Intl.DateTimeFormat("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          }).format(date)
        }
      },
      {
        id: 'paymentDetails',
        accessorKey: 'paymentDetails',
        header: L.admin_panel.payments.error_details,
        cell: ({ row }) => {
          const paymentDetails = row.original.paymentDetails

          if (!paymentDetails || paymentDetails == "") {
            return <>--</>
          }
        }
      }];

    // Add dynamic target field columns
    if (targetFields?.length) {
      targetFields.forEach(targetField => {
        baseColumns.push({
          accessorFn: (row) => row.targetFields?.[targetField.id],
          id: `targetField_${targetField.id}`,
          header: targetField.label,
          cell: ({ row }) => {
            const value = row.original.targetFields?.[targetField.id];
            if (value === undefined || value === null || value === '') {
              return <span className="text-gray-400 italic">--</span>;
            }

            return <CodeDisplay className='text-sm' copyable={false} displayMode="block" code={value} contentMode="wrap" variant="gray" />;
          }
        });
      });
    }

    // Add the view-application column at the end
    baseColumns.push({
      id: "view-application",
      header: L.admin_panel.payments.view_application,
      cell: ({ row }) => {
        const uuid = row.original.uuid

        // Link directly to admin panel payments page
        return (
          <NavLink
            to={`/admin-panel/payments/${uuid}`}
            target="_blank"
            className="text-[var(--ak-color-typography-link)] hover:text-[var(--ak-color-typography-link-hover)] font-medium inline-flex items-center gap-2"
          >
            {L.admin_panel.payments.view_application}
            <Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />
          </NavLink>
        )
      },
      enableSorting: false,
      enablePinning: true,
    });

    return baseColumns;
  }, [L, targetFields])

  useVisibilityPolling({
    onImmediate: () => fetchRecipientDetailsByBatch(batch_name),
    onInterval: () => fetchRecipientDetailsByBatch(batch_name),
    isActive: isOnPaymentBatchRecipientsPage,
    intervalMs: 2 * 60 * 1000
  })

  // Initialize column visibility from URL
  useEffect(() => {
    const urlData = getColumnsFromUrl();
    if (urlData.hidden && urlData.hidden.length > 0) {
      const allColumnIds = PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS.map(col => col.id).filter(Boolean) as string[];
      const validHiddenColumns = urlData.hidden.filter(col => allColumnIds.includes(col));
      
      const initialVisibility = Object.fromEntries(
        allColumnIds.map(colId => [colId, !validHiddenColumns.includes(colId)])
      );
      setColumnVisibility(initialVisibility);
    }
  }, [getColumnsFromUrl]);

  // Initialize target field columns as hidden by default
  useEffect(() => {
    if (targetFields?.length) {
      const targetFieldColumnIds = targetFields.map(
        field => `targetField_${field.id}`
      );

      // Only update if these columns haven't been set yet (preserve user choices)
      setColumnVisibility(prev => {
        const needsUpdate = targetFieldColumnIds.some(id => !(id in prev));
        if (!needsUpdate) return prev;

        const newVisibility = { ...prev };
        targetFieldColumnIds.forEach(id => {
          if (!(id in newVisibility)) {
            newVisibility[id] = false; // Hidden by default
          }
        });
        return newVisibility;
      });
    }
  }, [targetFields]);

  // Handle column visibility changes
  const handleColumnVisibilityChange = useCallback((visibility: Record<string, boolean>) => {
    setColumnVisibility(visibility);
    updateUrlWithColumns(visibility);
  }, [updateUrlWithColumns]);

  // Convert visibility state to hiddenCols for DataTable
  const hiddenColumns = Object.entries(columnVisibility)
    .filter(([_, visible]) => !visible)
    .map(([columnId]) => columnId);

  useEffect(() => {
    const recipientDetails = getBatchRecipientDetails(batch_name)
    setPaymentRecipientsDetails(recipientDetails)

  }, [getBatchRecipientDetails])

  const availablePaymentMethods = new Set(
    paymentRecipientsDetails.map((paymentRecipient) => paymentTypeToString(paymentRecipient.paymentMethod))
  )

  // Determine which columns to show based on pageConfig
  const visibleColumns = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, show nothing
      return [];
    }

    const config = pageConfig.columns;
    return PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS.filter((column) => {
      const id = column.id as string;

      // Always show these columns
      if (id === 'uuid' || id === 'firstName' || id === 'lastName' || id === 'view-application') return true;

      // Check config for other columns
      if (id === 'paymentStatus') return config.paymentStatus !== 'hidden';
      if (id === 'paymentAmount') return config.paymentAmount !== 'hidden';
      if (id === 'paymentInitiatedDate') return config.paymentInitiatied !== 'hidden';
      if (id === 'paymentMethod') return config.paymentMethod !== 'hidden';
      if (id === 'paymentScheduledFor') return config.paymentScheduledFor !== 'hidden';
      if (id === 'paymentDetails') return config.errorDetails !== 'hidden';

      // Show target field columns by default (can be toggled via column visibility)
      if (id?.startsWith('targetField_')) {
        return true;
      }

      // Default: show column if not explicitly configured
      return true;
    });
  }, [PAYMENT_BATCH_RECIPIENT_DETAILS_COLUMNS, pageConfig]);

  // Determine if search should be enabled
  const enableSearch = pageConfig?.searchBar !== 'hidden';

  // Determine if there are any available filters based on pageConfig
  const hasAvailableFilters = useMemo(() => {
    if (!pageConfig?.columns) {
      // If no config, all filters are available by default
      return true;
    }

    const columns = pageConfig.columns;
    // Check if at least one filterable column is visibleAndFilterable
    return columns.paymentStatus === 'visibleAndFilterable' ||
           columns.paymentMethod === 'visibleAndFilterable' ||
           columns.paymentInitiatied === 'visibleAndFilterable';
  }, [pageConfig]);

  const paymentBatchEmpty = !isPaymentBatchLoading && paymentRecipientsDetails.length == 0
  const paymentBatchEmptyState = (
    <div className="py-2 text-center text-gray-600">
      {L.admin_panel.payments.no_eligible_applicants}
    </div>
  )

  return (
    <div className="flex flex-col gap-y-1">
      <Card>
        <CardBody>
          {paymentBatchEmpty ? paymentBatchEmptyState : (
            <DataTable
              columns={visibleColumns}
              data={paymentRecipientsDetails}
              enableSearch={enableSearch}
              searchLabel={L.admin_panel.payments.search_recipients}
              columnPinningState={{
                left: ['uuid'],
                right: ['view-application']
              }}
              filters={hasAvailableFilters ? (table) => (
                <PaymentBatchRecipientFilter
                  table={table}
                  filters={filters}
                  setFilters={setFilters}
                  setActiveFilters={setActiveFilters}
                  availablePaymentMethods={availablePaymentMethods}
                  defaultTableColumns={pageConfig?.columns}
                />
              ) : undefined}
              activeFilters={activeFilters}
              dataFilteredOutMessage={L.admin_panel.payments.no_applicants_match_filter}
              resizableColumns
              loading={isPaymentBatchLoading || isConfigLoading}
              showPagination={true}
              lastUpdatedTimestamp={lastUpdatedTimestamp}
              hiddenCols={hiddenColumns}
              enableColumnVisibility={true}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              headerActionButtons={[toggleButton]}
            />
          )}
        </CardBody>
      </Card>
    </div>
  )
}
  